import {
  FormControl,
  FormLabel,
  HStack,
  Radio,
  RadioGroup,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { EGroupFindPermission } from '@/graphql-types/globalTypes';
import { InfoIcon } from '@/imports/ui/chakra/feather';
import { getPermissionLabel } from '@/utilities';

export const CanFindConfig: React.FC<{
  isEditing: boolean;
  availableFindPermissions: EGroupFindPermission[];
  onRequestFindChange?: (
    newValue: EGroupFindPermission
  ) => Promise<boolean>;
  isFindChangeLoading?: boolean;
}> = ({
  isEditing,
  availableFindPermissions,
  onRequestFindChange,
  isFindChangeLoading,
}) => {
  const { watch, setValue } = useFormContext();
  const findConfigValue = watch('canFindConfig');

  const FIND_TOOLTIPS: Partial<Record<EGroupFindPermission, string>> = {
    [EGroupFindPermission.LOGGED_IN]:
      'All logged-in users, including non-members, will see this group in their group list.',
    [EGroupFindPermission.MEMBER_STATUS_USERS]:
      'Only users with verified member status can find this group in search or listings.',
    [EGroupFindPermission.ADMINS]:
      'Only admins and current group members can see this group.',
    [EGroupFindPermission.GROUP_MEMBERS_ONLY]:
      'This group is hidden from everyone except users who are already in it.',
  };

  const applyFindChange = React.useCallback(
    (newValue: EGroupFindPermission) => {
      setValue('canFindConfig', newValue);
    },
    [setValue]
  );

  const requestFindChange = React.useCallback(
    async (newValue: EGroupFindPermission) => {
      if (newValue === findConfigValue || isFindChangeLoading) {
        return;
      }
      if (!onRequestFindChange) {
        applyFindChange(newValue);
        return;
      }
      const accepted = await onRequestFindChange(newValue);
      if (accepted) {
        applyFindChange(newValue);
      }
    },
    [
      applyFindChange,
      findConfigValue,
      isFindChangeLoading,
      onRequestFindChange,
    ]
  );

  return (
    <FormControl as="fieldset">
      <FormLabel as="legend">Who Can Find this Group?</FormLabel>
      {isEditing ? (
        <>
          <Controller
            name="canFindConfig"
            render={({ field: { value, ref } }) => (
              <RadioGroup
                value={value}
                onChange={(newValue) => {
                  void requestFindChange(newValue as EGroupFindPermission);
                }}
                ref={ref}
                isDisabled={isFindChangeLoading}
              >
                <VStack spacing="2" width="full" alignItems="start">
                  {availableFindPermissions.map((detail) => (
                    <Radio
                      key={detail}
                      aria-label={getPermissionLabel({ find: detail }) || ''}
                      value={detail}
                    >
                      <HStack spacing="1">
                        <Text as="span">
                          {getPermissionLabel({ find: detail })}
                        </Text>
                        <Tooltip label={FIND_TOOLTIPS[detail] || ''}>
                          <InfoIcon color="blue.500" />
                        </Tooltip>
                      </HStack>
                    </Radio>
                  ))}
                </VStack>
              </RadioGroup>
            )}
          />
          {isFindChangeLoading && (
            <Text color="gray.500" mt="2">
              Checking who would be affected…
            </Text>
          )}
        </>
      ) : (
        <Text color="gray.600">
          {getPermissionLabel({ find: findConfigValue })}
        </Text>
      )}
    </FormControl>
  );
};
